/**
 * Client-side view of request failures: every server `ErrorPayload`, plus two
 * transport outcomes the server never sends (`TIMEOUT`, `OFFLINE`).
 */
import type { ErrorPayload } from '@wordloom/engine';

export type ClientErrorCode = ErrorPayload['code'] | 'TIMEOUT' | 'OFFLINE';

export interface ClientErrorPayload extends Omit<ErrorPayload, 'code'> {
  code: ClientErrorCode;
}

/** An ack normalised for the UI: server errors and transport failures alike. */
// `{}` (not `object`) so `Result` with no payload is exactly `{ ok: true }`.
export type Result<T extends object = {}> = ({ ok: true } & T) | { ok: false; error: ClientErrorPayload };

export const OFFLINE_ERROR: ClientErrorPayload = {
  code: 'OFFLINE',
  message: 'You’re offline — reconnecting…',
};

export const TIMEOUT_ERROR: ClientErrorPayload = {
  code: 'TIMEOUT',
  message: 'The server didn’t answer in time. Please try again.',
};

/** True for failures where the intent may or may not have reached the server. */
export function isTransportError(error: ClientErrorPayload): boolean {
  return error.code === 'TIMEOUT' || error.code === 'OFFLINE';
}
